import React from "react";
import { SHEET_STYLING } from "../../../../constants";

/**
 * Utility functions for generating inline styles for sheet light DOM wrappers.
 *
 * These styles MUST be inline because:
 * 1. The sheet/backdrop are rendered via Portal to document.body (light DOM)
 * 2. ComponentUI.css is injected into Shadow DOM and cannot style light DOM elements
 * 3. Maximum z-index values ensure the sheet renders above all external site elements
 */

export interface BackdropStylesConfig {
  isVisible: boolean;
  isAnimating: boolean;
}

export interface SheetWrapperStylesConfig {
  side: "end" | "bottom";
  isVisible: boolean;
  isAnimating: boolean;
  customStyle?: React.CSSProperties;
}

/**
 * Creates inline styles for the backdrop wrapper element
 */
export const createBackdropStyles = ({
  isVisible,
  isAnimating,
}: BackdropStylesConfig): React.CSSProperties => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: SHEET_STYLING.zIndex.backdrop,
  pointerEvents: "auto", // Enable click detection for close-on-backdrop-click
  opacity: isAnimating ? 1 : 0,
  backgroundColor: SHEET_STYLING.backdrop.backgroundColor,
  transition: `opacity ${SHEET_STYLING.transitions.duration} ${SHEET_STYLING.transitions.easing}`,
  display: isVisible ? "block" : "none",
});

/**
 * Creates inline styles for the sheet wrapper element
 */
export const createSheetWrapperStyles = ({
  side,
  isVisible,
  isAnimating,
  customStyle = {},
}: SheetWrapperStylesConfig): React.CSSProperties => ({
  position: "fixed",
  zIndex: SHEET_STYLING.zIndex.sheet,
  pointerEvents: "auto", // Ensure sheet receives clicks
  ...(side === "end"
    ? {
        // Desktop: slide in from right
        top: 0,
        right: 0,
        bottom: 0,
        width: "fit-content",
        height: "100%",
        transform: isAnimating ? "translateX(0)" : "translateX(100%)",
        transition: `transform ${SHEET_STYLING.transitions.duration} ${SHEET_STYLING.transitions.easing}`,
      }
    : {
        // Mobile: slide up from bottom
        bottom: 0,
        left: 0,
        width: "100%",
        maxHeight: "70vh",
        overflowY: "auto",
        transform: isAnimating ? "translateY(0)" : "translateY(100%)",
        transition: `transform ${SHEET_STYLING.transitions.duration} ${SHEET_STYLING.transitions.easing}`,
      }),
  display: isVisible ? "block" : "none",
  ...customStyle, // Allow custom styles to override defaults
});
